import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileTextIcon, TrendingUpIcon, HomeIcon, EuroIcon, CalculatorIcon, AlertCircleIcon, GraduationCapIcon, CheckCircleIcon, SparklesIcon, ArrowRightIcon } from 'lucide-react';
import { useTransactions } from '../contexts/TransactionContext';
import { AgencyCommissionCalculator } from '../components/AgencyCommissionCalculator';
import { RentalCommissionCalculator } from '../components/RentalCommissionCalculator';
import { SaleCommissionCalculator } from '../components/SaleCommissionCalculator';
type ViewMode = 'simulators' | 'transactions' | 'agency';
type CalculatorTab = 'rental' | 'sale';
export function Transactions() {
  const {
    transactions
  } = useTransactions();
  const [viewMode, setViewMode] = useState<ViewMode>('simulators');
  const [activeCalculator, setActiveCalculator] = useState<CalculatorTab>('sale');
  const calculatorTabs = [{
    id: 'sale' as CalculatorTab,
    label: 'Vânzare',
    icon: TrendingUpIcon,
    gradient: 'from-orange-500 via-red-500 to-pink-500',
    iconBg: 'bg-gradient-to-br from-orange-100 to-red-100',
    iconColor: 'text-orange-600'
  }, {
    id: 'rental' as CalculatorTab,
    label: 'Închiriere',
    icon: HomeIcon,
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    iconBg: 'bg-gradient-to-br from-emerald-100 to-teal-100',
    iconColor: 'text-emerald-600'
  }];
  const filteredTransactions = useMemo(() => {
    return transactions;
  }, [transactions]);
  const totalValue = filteredTransactions.reduce((sum, t) => sum + t.transactionValue, 0);
  const totalCommission = filteredTransactions.reduce((sum, t) => sum + t.agreedCommission, 0);
  const stats = [{
    label: 'Total Tranzacții',
    value: filteredTransactions.length,
    icon: FileTextIcon,
    gradient: 'from-blue-500 to-indigo-600',
    bgGradient: 'from-blue-50 to-indigo-50',
    iconBg: 'from-blue-100 to-indigo-100'
  }, {
    label: 'Valoare Totala Tranzactionata',
    value: `${totalValue.toLocaleString('ro-RO')}€`,
    icon: EuroIcon,
    gradient: 'from-emerald-500 to-teal-600',
    bgGradient: 'from-emerald-50 to-teal-50',
    iconBg: 'from-emerald-100 to-teal-100'
  }, {
    label: 'Total Comisioane fara TVA',
    value: `${totalCommission.toLocaleString('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}€`,
    icon: TrendingUpIcon,
    gradient: 'from-purple-500 to-pink-600',
    bgGradient: 'from-purple-50 to-pink-50',
    iconBg: 'from-purple-100 to-pink-100'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-11689">
      {/* Hero Header with Gradient - Mobile Optimized */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278]" data-id="element-11690">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-11691" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-11692"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-id="element-11693">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-11694">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-4 sm:mb-6 shadow-2xl" data-id="element-11695">
              <CalculatorIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" data-id="element-11696" />
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 tracking-tight px-4" data-id="element-11697">
              Tranzacții & Simulatoare
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-2xl mx-auto px-4" data-id="element-11698">
              Exersează și înțelege procesul de calcul al comisioanelor într-un
              mediu de training sigur
            </p>
          </motion.div>
        </div>

        {/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0" data-id="element-11699">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-8 sm:h-12" data-id="element-11700"></svg>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 pb-12 sm:pb-16" data-id="element-11701">
        {/* Training Notice - Mobile Optimized */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-6 sm:mb-8 relative" data-id="element-11702">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-purple-500/20 rounded-2xl sm:rounded-3xl blur-xl" data-id="element-11703" />
          <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-slate-200 overflow-hidden" data-id="element-11704">
            <div className="absolute top-0 right-0 w-40 h-40 sm:w-64 sm:h-64 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full blur-3xl" data-id="element-11705" />
            <div className="relative p-5 sm:p-8" data-id="element-11706">
              <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-11707">
                <motion.div animate={{
                rotate: [0, 10, -10, 0]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 3
              }} className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg" data-id="element-11708">
                  <GraduationCapIcon className="w-6 h-6 sm:w-7 sm:h-7 text-white" data-id="element-11709" />
                </motion.div>
                <div className="flex-1" data-id="element-11710">
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3 flex items-center" data-id="element-11711">
                    Modul Training & Simulare
                    <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 ml-2 text-yellow-500" data-id="element-11712" />
                  </h3>
                  <p className="text-slate-600 mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base md:text-lg" data-id="element-11713">
                    Aceste simulatoare sunt{' '}
                    <span className="font-semibold text-blue-600" data-id="element-11714">
                      instrumente de training
                    </span>{' '}
                    pentru a te familiariza cu procesul de calcul al
                    comisioanelor. Exersează în siguranță înainte de a lucra cu
                    clienți reali.
                  </p>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-blue-100" data-id="element-11715">
                    <p className="text-xs sm:text-sm font-semibold text-slate-900 mb-2 sm:mb-3 flex items-center" data-id="element-11716">
                      <AlertCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600 flex-shrink-0" data-id="element-11717" />
                      Pentru tranzacții reale, asigură-te că ai:
                    </p>
                    <div className="space-y-2" data-id="element-11718">
                      {['Finalizat procesul de on-boarding', 'Completat cursul "Training și proceduri închiriere"'].map((item, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.5 + i * 0.1
                    }} className="flex items-start group" data-id="element-11719">
                          <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-emerald-600 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" data-id="element-11720" />
                          <span className="text-slate-700 text-xs sm:text-sm" data-id="element-11721">
                            {item}
                          </span>
                        </motion.div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main Navigation Tabs - Mobile Optimized */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="mb-6 sm:mb-10 overflow-x-auto pb-2" data-id="element-11722">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-1.5 sm:p-2 inline-flex border border-slate-200 min-w-full sm:min-w-0" data-id="element-11723">
            {[{
            id: 'simulators' as ViewMode,
            label: 'Simulare',
            fullLabel: 'Simulare Tranzacții',
            icon: CalculatorIcon,
            gradient: 'from-blue-600 to-indigo-600'
          }, {
            id: 'transactions' as ViewMode,
            label: `Tranzacții (${transactions.length})`,
            fullLabel: `Tranzacții (${transactions.length})`,
            icon: FileTextIcon,
            gradient: 'from-emerald-600 to-teal-600'
          }, {
            id: 'agency' as ViewMode,
            label: 'Încasări',
            fullLabel: 'Încasări Agent',
            icon: TrendingUpIcon,
            gradient: 'from-purple-600 to-pink-600'
          }].map(tab => {
            const Icon = tab.icon;
            const isActive = viewMode === tab.id;
            return <motion.button key={tab.id} onClick={() => setViewMode(tab.id)} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className={`relative px-3 sm:px-6 py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl font-bold transition-all flex items-center space-x-1.5 sm:space-x-2 whitespace-nowrap text-sm sm:text-base ${isActive ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg` : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-11724">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" data-id="element-11725" />
                  <span className="hidden sm:inline" data-id="element-11726">{tab.fullLabel}</span>
                  <span className="sm:hidden" data-id="element-11727">{tab.label}</span>
                  {isActive && <motion.div layoutId="activeTab" className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-lg sm:rounded-xl" transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30
              }} data-id="element-11728" />}
                </motion.button>;
          })}
          </div>
        </motion.div>

        {/* Simulators View - Mobile Optimized */}
        <AnimatePresence mode="wait" data-id="element-11729">
          {viewMode === 'simulators' && <motion.div key="simulators" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-11730">
              {/* Calculator Type Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-11731">
                {calculatorTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeCalculator === tab.id;
              return <motion.button key={tab.id} onClick={() => setActiveCalculator(tab.id)} whileHover={{
                scale: 1.02,
                y: -4
              }} whileTap={{
                scale: 0.98
              }} className={`relative overflow-hidden rounded-2xl sm:rounded-3xl transition-all ${isActive ? 'shadow-2xl' : 'shadow-lg hover:shadow-xl'}`} data-id="element-11732">
                      <div className={`absolute inset-0 bg-gradient-to-br ${tab.gradient} ${isActive ? 'opacity-100' : 'opacity-0'} transition-opacity`} data-id="element-11733" />
                      <div className={`relative bg-white ${isActive ? 'bg-opacity-0' : 'bg-opacity-100'} p-6 sm:p-8 transition-all`} data-id="element-11734">
                        <div className="flex items-center space-x-3 sm:space-x-4" data-id="element-11735">
                          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl ${isActive ? 'bg-white/20' : tab.iconBg} bg-gradient-to-br flex items-center justify-center transition-all flex-shrink-0`} data-id="element-11736">
                            <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${isActive ? 'text-white' : tab.iconColor}`} data-id="element-11737" />
                          </div>
                          <div className="flex-1 text-left" data-id="element-11738">
                            <h3 className={`text-lg sm:text-xl md:text-2xl font-bold mb-1 ${isActive ? 'text-white' : 'text-slate-900'}`} data-id="element-11739">
                              {tab.label}
                            </h3>
                            <p className={`text-xs sm:text-sm ${isActive ? 'text-white/80' : 'text-slate-600'}`} data-id="element-11740">
                              Simulează tranzacții de {tab.label.toLowerCase()}
                            </p>
                          </div>
                          <ArrowRightIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${isActive ? 'text-white' : 'text-slate-400'} flex-shrink-0`} data-id="element-11741" />
                        </div>
                      </div>
                    </motion.button>;
            })}
              </div>

              {/* Calculator Content */}
              <motion.div key={activeCalculator} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3
          }} data-id="element-11742">
                {activeCalculator === 'rental' && <RentalCommissionCalculator onNavigateToTransactions={() => setViewMode('transactions')} data-id="element-11743" />}
                {activeCalculator === 'sale' && <SaleCommissionCalculator onNavigateToTransactions={() => setViewMode('transactions')} data-id="element-11744" />}
              </motion.div>
            </motion.div>}

          {/* Agency Calculator View */}
          {viewMode === 'agency' && <motion.div key="agency" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-11745">
              <AgencyCommissionCalculator data-id="element-11746" />
            </motion.div>}

          {/* Transactions List View - Mobile Optimized */}
          {viewMode === 'transactions' && <motion.div key="transactions" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-11747">
              {/* Stats Cards - Mobile Optimized */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-11748">
                {stats.map((stat, index) => {
              const Icon = stat.icon;
              return <motion.div key={stat.label} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.1
              }} whileHover={{
                y: -4
              }} className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all" data-id="element-11749">
                      <div className={`absolute inset-0 bg-gradient-to-br ${stat.bgGradient}`} data-id="element-11750" />
                      <div className="relative p-5 sm:p-6" data-id="element-11751">
                        <div className="flex items-start justify-between mb-3 sm:mb-4" data-id="element-11752">
                          <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${stat.iconBg} flex items-center justify-center shadow-lg`} data-id="element-11753">
                            <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-slate-700" data-id="element-11754" />
                          </div>
                          <div className={`px-2 sm:px-3 py-1 rounded-full bg-gradient-to-r ${stat.gradient} text-white text-xs font-bold`} data-id="element-11755">
                            Simulat
                          </div>
                        </div>
                        <p className="text-xs sm:text-sm font-medium text-slate-600 mb-1" data-id="element-11756">
                          {stat.label}
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900" data-id="element-11757">
                          {stat.value}
                        </p>
                      </div>
                    </motion.div>;
            })}
              </div>

              {/* Transactions Table - Mobile Optimized */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border border-slate-200" data-id="element-11758">
                {filteredTransactions.length === 0 ? <div className="p-8 sm:p-16 text-center" data-id="element-11759">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200
              }} className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center" data-id="element-11760">
                      <FileTextIcon className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400" data-id="element-11761" />
                    </motion.div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2" data-id="element-11762">
                      Nicio tranzacție simulată
                    </h3>
                    <p className="text-slate-600 mb-4 sm:mb-6 text-sm sm:text-base" data-id="element-11763">
                      Începe să adaugi tranzacții din simulatoarele de mai sus
                    </p>
                    <button onClick={() => setViewMode('simulators')} className="inline-flex items-center space-x-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all text-sm sm:text-base min-h-[44px]" data-id="element-11764">
                      <CalculatorIcon className="w-5 h-5" data-id="element-11765" />
                      <span data-id="element-11766">Deschide Simulatoarele</span>
                    </button>
                  </div> : <div className="overflow-x-auto" data-id="element-11767">
                    <table className="w-full" data-id="element-11768">
                      <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200" data-id="element-11769">
                        <tr data-id="element-11770">
                          {['Nr.', 'Data', 'Tip', 'Client', 'Valoare', 'Comision fara TVA', 'Comision +TVA', 'Acțiuni'].map(header => <th key={header} className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap" data-id="element-11771">
                              {header}
                            </th>)}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-11772">
                        {filteredTransactions.map((transaction, index) => {
                    // Calculate commission without VAT and with VAT (21%)
                    const commissionWithoutVAT = transaction.agreedCommission;
                    const commissionWithVAT = commissionWithoutVAT * 1.21;
                    return <motion.tr key={transaction.id} initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: index * 0.05
                    }} className="hover:bg-slate-50 transition-colors group" data-id="element-11773">
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap" data-id="element-11774">
                                <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 text-xs sm:text-sm font-bold text-slate-700" data-id="element-11775">
                                  {index + 1}
                                </span>
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-slate-900" data-id="element-11776">
                                {new Date(transaction.date).toLocaleDateString('ro-RO')}
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap" data-id="element-11777">
                                <div className="flex items-center space-x-1.5 sm:space-x-2" data-id="element-11778">
                                  {transaction.type === 'rental' ? <>
                                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center" data-id="element-11779">
                                        <HomeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" data-id="element-11780" />
                                      </div>
                                      <span className="text-xs sm:text-sm font-semibold text-slate-900 hidden sm:inline" data-id="element-11781">
                                        Închiriere
                                      </span>
                                    </> : <>
                                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center" data-id="element-11782">
                                        <TrendingUpIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600" data-id="element-11783" />
                                      </div>
                                      <span className="text-xs sm:text-sm font-semibold text-slate-900 hidden sm:inline" data-id="element-11784">
                                        Vânzare
                                      </span>
                                    </>}
                                </div>
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-slate-900" data-id="element-11785">
                                {transaction.clientName}
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-slate-900" data-id="element-11786">
                                {transaction.transactionValue.toLocaleString('ro-RO')}
                                €
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap" data-id="element-11787">
                                <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-lg bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 text-xs sm:text-sm font-bold" data-id="element-11788">
                                  {commissionWithoutVAT.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                  €
                                </span>
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap" data-id="element-11789">
                                <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs sm:text-sm font-bold" data-id="element-11790">
                                  {commissionWithVAT.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                  €
                                </span>
                              </td>
                              <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm" data-id="element-11791">
                                <button className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-all" data-id="element-11792">
                                  Vezi
                                </button>
                              </td>
                            </motion.tr>;
                  })}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}